import React, {
  ChangeEvent,
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Dropdown, DropdownItem, DropdownList, Input } from '@cokitchen/cokitchen-components';
import { AddIcon, ArrowDownIcon } from 'cokitchen-icons';
import { ReactComponent as TooltipIcon } from 'assets/icons/tooltip.svg';
import MetadataTag from 'components/base/metadata-tag';
import CurrencyInput from 'components/base/currency-input';
import { AddStoreItemType, StoreItemType, VendorIngredientType } from 'models/store-item.model';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { storeItemWeightMeasurement } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';

type VendorType = {
  name: string;
  price: string;
  id: string;
};
type Props = {
  storeItem: StoreItemType;
  setStoreItem: (storeItem: StoreItemType) => void;
  handleDelete: () => void;
  index: number;
  vendorIngredientIdsToDelete: string[];
  setVendorIngredientIdsToDelete: Dispatch<SetStateAction<string[]>>;
};
const isKeyOfStoreItem = (key: string): key is keyof AddStoreItemType => {
  return ['name', 'quantity', 'vendor', 'amount', 'unit', 'category'].includes(key);
};

const EditStoreItemForm = ({
  storeItem,
  setStoreItem,
  vendorIngredientIdsToDelete,
  setVendorIngredientIdsToDelete
}: Props) => {
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const {
    isFetchingFormVendorsList,
    formVendorsList,
    isFetchingFormIngredientCategoryList,
    formIngredientCategoryList
  } = useDashboardContext();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { name, value } = target;
    if (!isKeyOfStoreItem(name)) return;
    setStoreItem({
      ...storeItem,
      [name]: value
    });
  };

  const defaultVendorIngredient: VendorIngredientType = {
    id: '',
    vendor_id: '',
    ingredient_id: '',
    created_at: '',
    updated_at: '',
    archived: false,
    gram_price: '',
    vendor: {
      id: '',
      name: ''
    }
  };

  const handleVendorChange = (item: VendorType, indexToUpdate: number) => {
    const updatedVendors = [...storeItem.vendor_ingredients];
    updatedVendors[indexToUpdate] = {
      ...updatedVendors[indexToUpdate],
      vendor_id: item.id,
      vendor: { id: item.id, name: item.name },
      gram_price: item.price
    };

    setStoreItem({
      ...storeItem,
      vendor_ingredients: updatedVendors
    });
  };

  const addVendor = () => {
    setStoreItem({
      ...storeItem,
      vendor_ingredients: [...storeItem.vendor_ingredients, defaultVendorIngredient]
    });
  };

  useEffect(() => {
    storeItem?.vendor_ingredients ? '' : setStoreItem({ ...storeItem, vendor_ingredients: [] });
  }, []);

  const unselectedVendors = useMemo(() => {
    const selectedIds = new Set(
      storeItem.vendor_ingredients.map((vendor) => vendor.vendor_id).filter(Boolean)
    );
    return formVendorsList.filter((formVendor) => !selectedIds.has(formVendor.id));
  }, [formVendorsList, storeItem.vendor_ingredients]);

  const filteredUnselectedVendors = useMemo(() => {
    const query = vendorSearch.trim().toLowerCase();
    if (!query) return unselectedVendors;
    return unselectedVendors.filter((vendor) => vendor.name?.toLowerCase().includes(query));
  }, [unselectedVendors, vendorSearch]);

  const deleteVendor = (index: number) => {
    setStoreItem({
      ...storeItem,
      vendor_ingredients: storeItem.vendor_ingredients.filter((_, i) => i !== index)
    });
  };

  const updateVendorPrice = (id: string, index: number, value: string) => {
    setStoreItem({
      ...storeItem,
      vendor_ingredients: storeItem.vendor_ingredients.map((vendor_ingredient, idx) =>
        idx === index ? { ...vendor_ingredient, gram_price: value } : vendor_ingredient
      )
    });
  };

  useEffect(() => {
    setCategory(storeItem.category);
  }, []);

  return (
    <div className='group w-full border-t border-[#EAEBEB] pt-[53px] mt-8 first:border-none first:mt-0 first:pt-0 relative'>
      {/* {storeItemArray.length > 1 && (
        <div className={`absolute top-6 right-0 z-10 ${index === 0 && '!top-[-29px]'}`}>
          <button onClick={handleDelete} className='cursor-pointer'>
            <p className='text-sm font-medium text-[#FF0000]'>- Remove Item</p>
          </button>
        </div>
      )} */}
      <div className='grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6 pt-3 -ml-8'>
        <Input
          design='bordered'
          label='Item Name'
          name='name'
          placeholder='Item Name'
          value={storeItem.name}
          onChange={handleChange}
        />
        <div className='relative'>
          <CurrencyInput
            label='Min. Qty'
            placeholder='Min. Qty'
            name='quantity'
            value={storeItem.min_quantity}
            onWheel={(e) => e.currentTarget.blur()}
            hideCurrency
            hasUnit
            unitValue={storeItem.unit_type}
            onUnitChange={(value) => {
              setStoreItem({
                ...storeItem,
                unit_type: value
              });
            }}
            unitOptions={storeItemWeightMeasurement}
            onChange={(value) => {
              setStoreItem({
                ...storeItem,
                min_quantity: Number(value)
              });
            }}
          />
        </div>

        <div>
          <p className='text-sm font-medium mb-1'>Category</p>
          <Dropdown
            visible={showCategoryDropdown}
            onClickOutside={() => setShowCategoryDropdown(false)}
            content={
              <div
                className='w-full flex justify-between items-center bg-[#FAFAFA] border-b border-b-[#D5D6D7] py-[15px] px-[14px] cursor-pointer'
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}>
                <p className={`truncate ${!storeItem.category && 'text-[#B5B8BA]'}`}>
                  {category || 'Select category'}
                </p>
                <ArrowDownIcon className='text-[#96999C]' />
              </div>
            }>
            <DropdownList className='min-w-[395px] max-w-[395px] pb-3'>
              {isFetchingFormIngredientCategoryList ? (
                <div className='py-2'>
                  <ActivityIndicator />
                </div>
              ) : formIngredientCategoryList?.length > 0 ? (
                formIngredientCategoryList.map((item) => (
                  <DropdownItem
                    key={item}
                    onClick={() => {
                      setCategory(item);
                      setStoreItem({
                        ...storeItem,
                        category: item
                      });
                      setShowCategoryDropdown(false);
                    }}>
                    {item}
                  </DropdownItem>
                ))
              ) : (
                <div className='py-4'>
                  <h4 className='text-center'>No categories</h4>
                </div>
              )}
              <div className='border-t border-t-[#ECEDEE] pt-4 px-3 mt-3'>
                <div className='flex rounded-md gap-2 bg-[#F7F7F8] h-[46px] w-full p-[10px]'>
                  <div className='w-[98px] h-[24px] flex gap-[2px] bg-[#EAEBEB] rounded-[3px] justify-center items-center'>
                    <AddIcon className='text-[#969A9C]' />
                    <p className='text-[#969A9C] text-xs'>Create new</p>
                  </div>
                  <input
                    type='text'
                    placeholder='Type something'
                    className='w-[55%] h-full bg-transparent outline-none text-sm'
                    value={newCategory}
                    onChange={(e) => {
                      setNewCategory(e.target.value);
                    }}
                  />
                  <div
                    className='w-[56px] h-[24px] flex justify-center cursor-pointer items-center rounded-[3px] shadow-sm border border-[#EAEBEB] bg-white'
                    onClick={() => {
                      setCategory(newCategory);
                      setStoreItem({
                        ...storeItem,
                        category: newCategory
                      });
                    }}>
                    <p className='text-[#5E6164] text-xs font-medium'>Save</p>
                  </div>
                </div>
              </div>
            </DropdownList>
          </Dropdown>
        </div>
        <Input
          design='bordered'
          label={`Unit per (${storeItem.unit_type})`}
          placeholder='00'
          type='number'
          name='unit'
          onWheel={(e) => e.currentTarget.blur()}
          value={storeItem.unit_weight}
          onChange={(e) => {
            setStoreItem({
              ...storeItem,
              unit_weight: e.target.value
            });
          }}
        />

        <Input
          design='bordered'
          label='Shelf Life'
          placeholder='0'
          type='number'
          name='shelfLife'
          value={storeItem.shelfLife}
          onChange={(e) => setStoreItem({ ...storeItem, shelfLife: Number(e.target.value) })}
        />
      </div>
      <div className='grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6 pt-3 -ml-8'>
        {storeItem.vendor_ingredients.filter(
          (vendor_ingredient) => !vendorIngredientIdsToDelete.includes(vendor_ingredient.id)
        ).length > 0 &&
          storeItem.vendor_ingredients
            .filter(
              (vendor_ingredient) => !vendorIngredientIdsToDelete.includes(vendor_ingredient.id)
            )
            .map((vendor_ingredient, vendor_ingredient_index) => {
              return (
                <Fragment key={vendor_ingredient.id}>
                  <div className='relative'>
                    {storeItem.vendor_ingredients.filter(
                      (vendor_ingredient) =>
                        !vendorIngredientIdsToDelete.includes(vendor_ingredient.id)
                    ).length > 1 && (
                      <button
                        onClick={() =>
                          vendor_ingredient.id
                            ? setVendorIngredientIdsToDelete((prev) => [
                                ...prev,
                                vendor_ingredient.id
                              ])
                            : deleteVendor(vendor_ingredient_index)
                        }
                        className='cursor-pointer absolute right-0'>
                        <p className='text-sm font-medium text-[#FF0000]'>- Remove</p>
                      </button>
                    )}

                    <div className='flex items-center gap-1 mb-1 '>
                      <p className='text-sm font-medium'>Vendor</p>
                      {vendor_ingredient_index == 0 && (
                        <div>
                          <Dropdown content={<TooltipIcon />} tooltip placement='top'>
                            <div className='bg-white rounded-md p-3 h-[58px] shadow'>
                              <p className='text-gray-9 text-sm'>
                                Select vendors with the highest selling price first
                              </p>
                            </div>
                          </Dropdown>
                        </div>
                      )}
                    </div>

                    <Dropdown
                      disabled={!!vendor_ingredient.id}
                      content={
                        <div className='flex justify-between items-center bg-[#FAFAFA] border-b border-b-[#D5D6D7] py-[15px] px-[14px] cursor-pointer'>
                          {
                            <Fragment>
                              {vendor_ingredient.vendor_id ? (
                                <div className='flex gap-2 flex-wrap'>
                                  <MetadataTag
                                    key={vendor_ingredient.vendor_id}
                                    name={vendor_ingredient.vendor.name}
                                    // hasDeleteBtn
                                  />
                                </div>
                              ) : (
                                <div className='w-full flex justify-between items-center'>
                                  <p className='text-[#B5B8BA] truncate'>Select vendor</p>
                                  <ArrowDownIcon className='text-[#96999C]' />
                                </div>
                              )}
                            </Fragment>
                          }
                        </div>
                      }>
                      <DropdownList className='vendors-array-container'>
                        <div className='px-3 pt-3'>
                          <Input
                            type='search'
                            placeholder='Search vendors'
                            inputClass='h-[36px]'
                            value={vendorSearch}
                            onChange={(e) => setVendorSearch(e.target.value)}
                          />
                        </div>
                        <div className='overflow-y-scroll h-[257px]'>
                          {formVendorsList?.length === 0 && isFetchingFormVendorsList ? (
                            <div className='py-3'>
                              <ActivityIndicator />
                            </div>
                          ) : filteredUnselectedVendors?.length > 0 ? (
                            filteredUnselectedVendors.map((item) => (
                              <DropdownItem key={item.id}>
                                <div
                                  className='flex gap-1 items-center'
                                  onClick={() => {
                                    handleVendorChange(
                                      { id: item.id, name: item.name, price: '' },
                                      vendor_ingredient_index
                                    );
                                    setVendorSearch('');
                                  }}>
                                  {/* <Input
                                    type='checkbox'
                                    className='mr-1'
                                    checked={vendor_ingredient.vendor_id === item.id}
                                    readOnly
                                  /> */}
                                  <span className='text-[#212224]'>{item.name}</span>
                                </div>
                              </DropdownItem>
                            ))
                          ) : (
                            <div className='py-3'>
                              <h4 className='text-center'>No result</h4>
                            </div>
                          )}
                          {formVendorsList?.length > 0 && isFetchingFormVendorsList && (
                            <div className='py-3'>
                              <ActivityIndicator />
                            </div>
                          )}
                        </div>
                      </DropdownList>
                    </Dropdown>
                  </div>
                  {/* <div className='relative'>
                    <Input
                      design='bordered'
                      label="Vendor"
                      type='text'
                      name='vendor'
                      onWheel={(e) => e.currentTarget.blur()}
                      value={vendor_ingredient.vendors.name}
                      disabled
                    />
                  </div> */}
                  <CurrencyInput
                    label='Vendor’s price'
                    placeholder='00'
                    name='amount'
                    onWheel={(e) => e.currentTarget.blur()}
                    value={vendor_ingredient.gram_price}
                    onChange={(value) =>
                      updateVendorPrice(vendor_ingredient.id, vendor_ingredient_index, value)
                    }
                  />
                </Fragment>
              );
            })}
        <button onClick={addVendor} className='text-[#0080E5] mt-2 font-medium text-sm w-max'>
          + Add Vendor
        </button>
      </div>

      {/* <div>
          <div className='flex items-center gap-1 mb-1'>
            <p className='text-sm font-medium'>Vendor</p>
            <div>
              <Dropdown content={<TooltipIcon />} tooltip placement='top'>
                <div className='bg-white rounded-md p-3 h-[58px] shadow'>
                  <p className='text-gray-9 text-sm'>
                    Ensure to select vendor with the lowest selling <br /> price
                  </p>
                </div>
              </Dropdown>
            </div>
          </div>
          <Dropdown
            content={
              <div className='flex justify-between items-center bg-[#FAFAFA] border-b border-b-[#D5D6D7] py-[15px] px-[14px] cursor-pointer'>
                {
                  <Fragment>
                    {storeItem.vendor_ingredients?.length ? (
                      <div className='flex gap-2 flex-wrap'>
                        {storeItem.vendor_ingredients.map((item) => {
                          return (
                            <MetadataTag
                              key={item.id}
                              name={`${item.vendors.name} (${item.gram_price}/${storeItem.unit_type})`}
                              onDelete={() => {
                                deleteVendor(item.id);
                              }}
                              hasDeleteBtn
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className='w-full flex justify-between items-center'>
                        <p className='text-[#B5B8BA] truncate'>Select vendor</p>
                        <ArrowDownIcon className='text-[#96999C]' />
                      </div>
                    )}
                  </Fragment>
                }
              </div>
            }>
            <DropdownList className='vendors-array-container'>
                {formVendorsList?.length === 0 && isFetchingFormVendorsList ? (
                  <div className='py-3'>
                    <ActivityIndicator />
                  </div>
                ) : formVendorsList?.length > 0 ? (
                  formVendorsList.map((item) => (
                    <DropdownItem key={item.id}>
                      <div
                        className='flex gap-1 items-center'
                        onClick={() => {
                          // handleVendorChange({ name: item.name, price: '0/gram', id: item.id });
                        }}>
                        <Input
                          type='checkbox'
                          className='mr-1'
                          checked={storeItem.vendor_ingredients?.some((vendor_ingredient) => vendor_ingredient.vendors.id === item.id)}
                          readOnly
                        />
                        <span className='text-[#212224]'>{item.name}</span>
                        <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                        <span className='text-[#6F7376] text-xs'>0/gram</span>
                      </div>
                    </DropdownItem>
                  ))
                ) : (
                  <div className='py-3'>
                    <h4 className='text-center'>No result</h4>
                  </div>
                )}
                {isFetchingFormVendorsList && (
                  <div className='py-3'>
                    <ActivityIndicator />
                  </div>
                )}
            </DropdownList>
          </Dropdown>
        </div> */}
    </div>
  );
};

export default EditStoreItemForm;
